import * as com from "../../Common"
import {
  calculateDividendIncome,
  calculateOtherIncome,
} from "../../Interview/Income"

export async function fetchLoanOverviewV2(loanId, token) {
  const response = await fetch(`/los/v2/loans/${loanId}/overview`, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + token,
      Cache: "no-cache",
    },
  })
  if (response.status !== 200) {
    throw new Error(`overview request failed: ${response.status}`)
  }
  const js = await response.json()
  if (js.Status !== "OK") {
    throw new Error(js.Text || "overview request failed")
  }
  return js
}

export function roleLabelForPerson(person, coborrowerCount, coborrowerIndex) {
  if (person.isPrimary || person.role === "borrower") {
    return "Primary borrower"
  }
  return coborrowerCount > 1 ? `Co-borrower ${coborrowerIndex}` : "Co-borrower"
}

export function personDisplayName(person, fallback = "") {
  const name = `${person.firstName || ""} ${person.lastName || ""}`.trim()
  return name || fallback
}

function hasFilledEmploymentRow(occ) {
  if (!occ || typeof occ !== "object") return false
  const t = (v) => typeof v === "string" && v.trim() !== ""
  if (t(occ.employername) || t(occ.positiontitle) || t(occ.from)) return true
  if (occ.selfemployed && t(occ.incomeorloss)) return true
  const inc = occ.income || {}
  return ["base", "overtime", "bonuses", "commissions", "military", "other"].some((k) =>
    t(inc[k])
  )
}

/** V2 1003 has no employed/unemployed toggle on 1b; infer from filled rows when flag is stale. */
export function resolveHasOccupation(borrower) {
  const stored = borrower?.occupation?.hasoccupation
  if (stored === "employed" || stored === "retired") return stored
  const has1b = hasFilledEmploymentRow(borrower?.occupation)
  const has1c =
    Array.isArray(borrower?.otheroccupations) &&
    borrower.otheroccupations.some(hasFilledEmploymentRow)
  if (has1b || has1c) return "employed"
  if (stored === "unemployed") return "unemployed"
  if (!stored) return null
  return stored
}

/**
 * URLA-aligned employment income for Overview V2 (1b + 1c).
 * See web/docs/los-application-v2/overview-v2-income-vs-legacy.md
 */
export function calculateEmploymentIncomeV2(borrower) {
  if (resolveHasOccupation(borrower) !== "employed") {
    return 0
  }
  let total = employmentEntryMonthlyIncome(borrower.occupation)
  const others = borrower.otheroccupations
  if (Array.isArray(others)) {
    for (const entry of others) {
      total += employmentEntryMonthlyIncome(entry)
    }
  }
  return total
}

/** V2 1003 collects incomeorloss for every self-employed row (1b/1c), regardless of ownership %. */
export function usesSelfEmploymentIncomeOrLoss(emp) {
  return !!emp?.selfemployed
}

function monthlyIncomeOrLoss(emp) {
  const v = parseFloat(emp.incomeorloss)
  if (Number.isNaN(v)) return 0
  if (emp.incomeorlossperiod === "yearly") return v / 12
  return v
}

function grossMonthlyIncome(employment) {
  const tokens = ["base", "commissions", "overtime", "bonuses", "military", "other"]
  return tokens.reduce((sum, token) => sum + getIncomeType(employment, token), 0)
}

function employmentEntryMonthlyIncome(emp) {
  if (!emp) return 0
  if (usesSelfEmploymentIncomeOrLoss(emp)) {
    return monthlyIncomeOrLoss(emp)
  }
  return grossMonthlyIncome(emp)
}

function getIncomeType(employment, token) {
  switch (employment.income?.[token + "monthly"]) {
    case "monthly": {
      const v = parseFloat(employment.income[token])
      return Number.isNaN(v) ? 0 : v
    }
    case "yearly": {
      const v = parseFloat(employment.income[token])
      return Number.isNaN(v) ? 0 : v / 12
    }
    case "hourly": {
      const v = parseFloat(employment.income[token])
      const h = parseFloat(employment.income[token + "hoursperweek"])
      return Number.isNaN(v) || Number.isNaN(h) ? 0 : (v * h * 52) / 12
    }
    default:
      return 0
  }
}

/** Overview V2 income card: URLA 1b/1c employment + legacy 1e dividend/other helpers. */
export function incomeFromProfile(profile) {
  const borrower = normalizeProfile(profile)
  const app = { borrower }
  return {
    employment: calculateEmploymentIncomeV2(borrower),
    dividends: calculateDividendIncome(app, "borrower"),
    other: calculateOtherIncome(app, "borrower"),
    hasOccupation: resolveHasOccupation(borrower),
    occupation: borrower?.occupation || {},
    otherOccupations: borrower?.otheroccupations || [],
  }
}

function normalizeProfile(profile) {
  let p = profile
  if (typeof p === "string") {
    try {
      p = JSON.parse(p)
    } catch (e) {
      p = null
    }
  }
  if (!p || typeof p !== "object") {
    return {
      occupation: { hasoccupation: null, income: {} },
      otheroccupations: null,
      income: { hasdividends: false, otherincome: null },
    }
  }
  const occupation = p.occupation || {}
  return {
    ...p,
    occupation: {
      ...occupation,
      income: { ...(occupation.income || {}) },
    },
    otheroccupations: p.otheroccupations || null,
    income: {
      hasdividends: false,
      otherincome: null,
      ...(p.income || {}),
    },
  }
}

export function formatMoney(amount, digits = 2) {
  const n = Number(amount)
  if (Number.isNaN(n)) return "$0.00"
  return `$${com.commaizeFloat(n.toFixed(digits))}`
}
